import React, { useContext } from 'react';
import { MultiElementContainer } from './MultiElementContainer';
import { CaseDetailsContext } from '../../providers/CaseDetailsProvider';
import { CaseSummaryConstants } from 'src/constants/ProjectConstants';
import styled from 'styled-components';
import { useUpdateCaseMutation } from 'src/state/slice/caseLifecycleApi';
import {
  CaseUpdateType,
  IUpdateCaseRequest,
} from 'src/state/slice/models/CaseLifecycleTypes';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { logger } from 'src/logger';
import { KatAlert, KatSpinner } from '@amzn/katal-react';
import { deepMergeObject } from 'src/utilities/HelperUtil';
import { TranslationContext } from 'src/state/providers/TranslationProvider';

const DynamicPanelLayout = styled.div`
  position: sticky;
  top: 1rem;
  z-index: 2;
`;

const DynamicPanel = () => {
  const UserContext = useContext(AuthContext);
  const { ddmAttributes, caseId, caseDetailsResponse } =
    useContext(CaseDetailsContext);
  const [
    updateCase,
    { isLoading, isError: isUpdateCaseError, error: updateCaseError },
  ] = useUpdateCaseMutation();

  /**
   * Once case summary is clicked and all validations are passed. This function will be executed.
   * @param data. The data which will be passed to the backend as payload.
   */
  const onCaseSummaryClicked = (data: any) => {
    const mergedData = deepMergeObject(
      ddmAttributes?.caseSummaryFormData,
      data,
    );
    logger.info(
      `Updating the Case: ${caseId} by User: ${UserContext.customerId} with the data`,
      {
        data,
        mergedData,
      },
    );

    const updateCaseRequest: IUpdateCaseRequest = {
      caseId: caseId,
      userId: UserContext.customerId,
      accountId: UserContext.preferredAccountId,
      clientId: UserContext.clientId,
      caseUpdateTypeList: [CaseUpdateType.CASE_OVERVIEW_UPDATE],
      caseOverviewData: JSON.stringify(mergedData),
    };
    logger.info(
      `API Request for updating the Case: ${caseId} by User: ${UserContext.customerId} with the payload`,
      {
        updateCaseRequest,
      },
    );
    updateCase(updateCaseRequest);
  };
  if (isUpdateCaseError) {
    logger.error('Unable to update the case. Error: ', { updateCaseError });
  }
  let defaultFormData = caseDetailsResponse?.caseOverviewData
    ? caseDetailsResponse.caseOverviewData
    : ddmAttributes?.caseSummaryFormData;

  if (isLoading) {
    return <KatSpinner size="small" />;
  }
  const { caseSummaryBundle, messageBundle } = useContext(TranslationContext);

  return (
    <DynamicPanelLayout>
      {isUpdateCaseError && (
        <KatAlert
          variant="danger"
          description={messageBundle.getMessage(
            CaseSummaryConstants.ERROR_MESSAGE_UNABLE_TO_UPDATE_DATA,
          )}
        />
      )}
      <MultiElementContainer
        coreSchema={ddmAttributes?.caseSummarySchema!}
        uiSchema={ddmAttributes?.caseSummaryUiSchema!}
        defaultFormData={defaultFormData}
        formTitle={caseSummaryBundle.getMessage(
          CaseSummaryConstants.EDIT_CASE_OVERVIEW,
        )}
        onUpdate={onCaseSummaryClicked}
      />
    </DynamicPanelLayout>
  );
};

export { DynamicPanel };
